import {Injectable} from '@angular/core';
import Keycloak, {KeycloakInitOptions} from "keycloak-js";
import {JwtHelperService} from "@auth0/angular-jwt";
import {LocalStorageService} from "@app/core/local-storage/local-storage.service";
import {KeycloakOptions} from "keycloak-angular/lib/core/interfaces/keycloak-options";
import {environment} from "@env/environment";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Observable, of} from "rxjs";
import {GrowlService, TokenType} from "@app/core";
import {QlikAppName} from '@app/shared/enums/qlik-app-name.enum';
import {DefaultOrg, GrowlType, RouteLink} from "@app/shared";
import {KeycloakActionType} from "@app/keycloak/keycloak-action-type";
import {Router} from "@angular/router";
import {catchError, map, tap} from "rxjs/operators";
import {KeycloakService} from "keycloak-angular";
import {Subject} from "rxjs";

enum KeycloakOption {
  Config = "KeycloakConfig",
  InitOptions = "KeycloakInitOptions"
}

@Injectable({
  providedIn: 'root'
})
export class KeycloakSupportService {


  static http: HttpClient;

  get keycloakEvents(): Subject<KeycloakSupportEvent> {
    return this._keycloakEvents;
  }

  private _keycloakEvents;

  static SsoIdleTimeout = environment.sso.SsoIdleTimeout;
  static SsoMaxTimeout = environment.sso.SsoMaxTimeout;
  static AccessTokenTimeout = environment.sso.AccessTokenTimeout;
  static SsoIdleTimeoutWarning = environment.sso.SsoIdleTimeoutWarning;
  static SsoMaxTimeoutWarning = environment.sso.SsoMaxTimeoutWarning;

  static tokenRefreshed = 0;
  static ssoIdleStart: Date;
  static ssoMaxStart: Date;
  static accessTokenStart: Date;

  static ssoIdleWarningHandler: any;
  static ssoIdleExpireHandler: any;

  MIN_VALIDITY_FOR_FORCE_UPDATE_TOKEN = 31536000;  // 365 days; no token should have older than 365 days

  jwtHelper = new JwtHelperService();

  constructor(private router: Router,
              private keycloakAngular: KeycloakService,
              private localStorage: LocalStorageService,
              private growl: GrowlService) {

    this._keycloakEvents = new Subject();
  }

  /**
   * validate whether the input realm exists in keycloak
   */
  isValidRealm(realm: string): Observable<boolean> {
    const authUrl = realm == DefaultOrg.Realm ? environment.keycloakConfig.authServerUrlForCsr : environment.keycloakConfig.authServerUrl;

    const url = `${authUrl}/realms/${realm}`;

    return KeycloakSupportService.http.get<boolean>(url)
      .pipe(
        tap(suc => suc.toString().includes(realm)),
        map(() => true),
        catchError(() => of(false))
      );
  }

  /**
   * handle initial login or reload page
   */
  handleInitialLoginOrReload(): void {
    console.log("handleInitialLoginOrReload: looks like first time init or page re-reload...");
    const keycloakOptions: KeycloakOptions = this.getKeycloakOptions();
    if (keycloakOptions.config && keycloakOptions.initOptions) {
      console.log("keycloakAngular.init() using saved options...");
      this.keycloakAngular.init(keycloakOptions).then(() => {
        console.log("keycloakAngular.init() completed...");
        if ((!this.keycloakAngular.getKeycloakInstance().token || this.keycloakAngular.isTokenExpired())) {
          console.log("tokens not available. invoking autoLoginOrRouteToCheckAuth() after init...");
          this.autoLoginOrRouteToCheckAuth(keycloakOptions);
        }
        // if token is successfully posted, then OnAuthSuccess event is triggered which in turn invoke processAfterAuth()
      }).catch(() => { // when init(), post token will result in an error
        console.log("handleInitialLoginOrReload: invoking autoLoginOrRouteToCheckAuth() after error in init...");
        this.autoLoginOrRouteToCheckAuth(keycloakOptions);
      });
    } else {
      console.log("handleInitialLoginOrReload: keycloakOptions not available. invoking autoLoginOrRouteToCheckAuth()...");
      this.autoLoginOrRouteToCheckAuth();
    }
  }

  /**
   * initialize keycloak and direct the url to keycloak login page
   */
  handleKeycloakLoginRequest(realm: string): void {
    const options = this.buildKeycloakOptionsForInit(realm);

    void this.keycloakAngular.init(options).then((options) => {
      console.log("keycloakAngular.init.then() ...");
      let keycloakInstance = this.keycloakAngular.getKeycloakInstance();
      // FixMe: change the redirectUri to point to home instead of the check-auth component
      void this.keycloakAngular.login(this.buildKeycloakLoginOptions()).then(() => {
        console.log("handleKeycloakLoginRequest: keycloakAngular.login then()");
      }).finally(() => {
        console.log("handleKeycloakLoginRequest: keycloakAngular.login finally()");
      }).catch(() => {
        this.growl.add(GrowlType.Error, "Specified org does not exist!");
        console.log("handleKeycloakLoginRequest: keycloakAngular.login catch()");
      });
    });
  }

  /**
   * handle redirection from the keycloak login page. re-init the keycloak using the login session; this will load the tokens
   */
  handleKeycloakReturn(realm: string): void {
    console.log("handleKeycloakReturn: returning from keycloak login");
    const keycloakInstance = this.keycloakAngular.getKeycloakInstance();
    if (keycloakInstance) {
      console.log("keycloakInstance exists BEFORE keycloakAngular.init() !!");
      this.saveKeycloakOptions(keycloakInstance);
    }

    const options = this.buildKeycloakOptionsForInit(realm);
    void this.keycloakAngular.init(options).then(() => {
      console.log("handleKeycloakReturn: keycloakAngular.init.then() completed...");
    });
  }

  /**
   * Keycloak logout is invoked and redirected back. Simply route to the check-auth page.
   */
  handleKeycloakLogout(realm: string): void {
    console.log("handleKeycloakLogout: handling logout redirection by routing to check-auth...");
    this.resetKeycloakOptions(realm);
    void this.router.navigateByUrl(`${RouteLink.CheckAuth}?realm=${realm}`);
  }

  resetKeycloakOptions(realm: string): void {
    console.log("resetKeycloakOptions()...");

    const authUrl = realm == DefaultOrg.Realm ? environment.keycloakConfig.authServerUrlForCsr : environment.keycloakConfig.authServerUrl;

    const config: Keycloak.KeycloakConfig = {
      url: environment.keycloakConfig.authServerUrl,
      realm: realm,
      clientId: environment.keycloakConfig.clientId
    };
    this.localStorage.add(KeycloakOption.Config, config);

    const initOptions: KeycloakInitOptions = {
      token: null,
      refreshToken: null,
      idToken: null,
      checkLoginIframe: false,
      responseMode: "fragment"
    };

    this.localStorage.add(KeycloakOption.InitOptions, initOptions);
  }

  saveKeycloakOptions(instance: Keycloak.KeycloakInstance): void {
    const config: Keycloak.KeycloakConfig = {
      url: instance.authServerUrl,
      realm: instance.realm,
      clientId: instance.clientId
    };
    this.localStorage.add(KeycloakOption.Config, config);

    const initOptions: KeycloakInitOptions = {
      token: instance.token,
      refreshToken: instance.refreshToken,
      idToken: instance.idToken,
      checkLoginIframe: false,
      responseMode: "fragment"
    };
    this.localStorage.add(KeycloakOption.InitOptions, initOptions);
  }

  /**
   * return saved keycloak config and initOptions. If access and refresh tokens are expired, set the tokens null
   */
  getKeycloakOptions(): KeycloakOptions {
    const config = this.localStorage.get(KeycloakOption.Config);
    let initOptions = <KeycloakInitOptions>this.localStorage.get(KeycloakOption.InitOptions);

    const isTokenExpired = initOptions == null ? true : this.jwtHelper.isTokenExpired(initOptions.token);
    const isRefreshTokenExpired = initOptions == null ? true : this.jwtHelper.isTokenExpired(initOptions.refreshToken);
    if (initOptions && isTokenExpired && isRefreshTokenExpired) {
      initOptions.token = null;
      initOptions.refreshToken = null;
      initOptions.idToken = null;
    }
    return <KeycloakOptions>{config, initOptions};
  }

  validSavedAccessOrRefreshToken(): boolean {
    const keycloakOptions = this.getKeycloakOptions();
    const isTokenExpired = keycloakOptions.initOptions == null ? true : this.jwtHelper.isTokenExpired(keycloakOptions.initOptions.token);
    const isRefreshTokenExpired = keycloakOptions.initOptions == null ? true : this.jwtHelper.isTokenExpired(keycloakOptions.initOptions.refreshToken);

    return !isTokenExpired || !isRefreshTokenExpired;
  }

  /**
   * if auto login is enabled, then obtain the token and re-initialize the keycloak
   * @param options
   * @param noKeycloakInit - used for testing; keycloak needs to be re-initialize after obtaining tokens manually
   */
  autoLogin(options: KeycloakOptions, noKeycloakInit?: boolean): void {
    if (environment.keycloakConfig.autoLogin) {
      const username = environment.keycloakConfig.userId;
      const password = environment.keycloakConfig.password;
      const realm = environment.keycloakConfig.realm;
      const clientId = environment.keycloakConfig.clientId;
      this.obtainToken(username, password, environment.keycloakConfig.authServerUrl, realm, clientId).subscribe((tokens: string[]) => {
        if (options && tokens && !noKeycloakInit) {
          options.initOptions.token = <string>tokens[TokenType.access];
          options.initOptions.refreshToken = <string>tokens[TokenType.refresh];
          options.initOptions.idToken = <string>tokens[TokenType.id];
          void this.keycloakAngular.init(options).then(() => {
            console.log("keycloakAngular.init() completed after obtaining token...");
          });
        } else {
          console.error("obtainToken() called but options/tokens are not good...");
        }
      });
    }
  }

  updateToken() {
    void this.keycloakAngular.updateToken().then(refresh => {
      if (refresh) {
        console.log("token updated");
        this.resetSsoIdle();
      } else {
        console.log("token update failed!");
      }
    });
  }

  forceUpdateToken(): Promise<boolean> {
    // ToDo nivas: rel-1.3 test it to make sure org creationg works using this method, then just call keycloakAngular.updateToken directly
    // then, you can remove this method
    // return this.keycloakAngular.updateToken(this.MIN_VALIDITY_FOR_FORCE_UPDATE_TOKEN);
    //   this combination has the min validity  "keycloak-angular": "^9.1.0" and "keycloak-js": "^17.0.0",
    // the latest does not keycloak-angular 9.3.0 and keycloak-js 17.0.1
    return this.keycloakAngular.updateToken(this.MIN_VALIDITY_FOR_FORCE_UPDATE_TOKEN);
  }

  resetSsoIdle() {
    clearTimeout(KeycloakSupportService.ssoIdleWarningHandler);
    clearTimeout(KeycloakSupportService.ssoIdleExpireHandler);
    this._keycloakEvents.next({
      args: true,
      type: KeycloakSupportEventType.OnSsoIdle
    });
    KeycloakSupportService.ssoIdleWarningHandler = setTimeout(() => {
        this._keycloakEvents.next({args: true, type: KeycloakSupportEventType.OnSsoIdleWarning});
      },
      (KeycloakSupportService.SsoIdleTimeout - KeycloakSupportService.SsoIdleTimeoutWarning) * 1000);
  }

  /**
   * set the redirect url so that the app component can route the check-auth component
   */
  logout(): Promise<void> {
    const redirectUri = this.buildRedirectUriForLogout();
    return this.keycloakAngular.logout(redirectUri);
  }

  punchOutToQlik(component: QlikAppName): void {
    let header: HttpHeaders = new HttpHeaders();
    header = header.set('Content-Type', 'application/json');
    header = header.append('X-Qlik-Xrfkey', 'ABCDEFG123456789');

    // let prefix = 'analytics' + '/' + this.user.info.realm.trim().toLowerCase();
    const prefix = 'analytics' + '/' + this.keycloakAngular.getKeycloakInstance().realm.toLocaleLowerCase();

    const serverName = environment.rootUrl;
    const urlPrefix = `${serverName}/${prefix}/sense/app/`;
    let url;
    KeycloakSupportService.http.get(`${serverName}/${prefix}/qrs/about?xrfkey=ABCDEFG123456789`, {
      headers: header,
      observe: 'response',
      withCredentials: true
    }).subscribe(() => {
      if (component === QlikAppName.Hub) {
        url = `${serverName}/${prefix}/hub/my/work`;
      } else if (component === QlikAppName.Hcm) {
        url = `${urlPrefix}${environment.HCM_DASHBOARD_APP_ID}`;
      } else if (component === QlikAppName.Project) {
        url = `${urlPrefix}${environment.PROJECT_DASHBOARD_APP_ID}`;
      } else if (component === QlikAppName.Finance) {
        url = `${urlPrefix}${environment.FINANCE_DASHBOARD_APP_ID}`;
      } else if (component === QlikAppName.Executive) {
        url = `${urlPrefix}${environment.EXECUTIVE_DASHBOARD_APP_ID}`;
      } else if (component === QlikAppName.Procurement) {
        url = `${urlPrefix}${environment.PROCUREMENT_DASHBOARD_APP_ID}`;
      }

      window.open(url, '_blank');
    });
  }

  prettyText(text) {
    return text
      .split(':')
      .map((v) => `<span class="countdown-item">${v}</span>`)
      .join(':');
  }

  /**
   * obtain keycloak tokens by authenticating using userid and passwd, bypassing the login page
   */
  private obtainToken(username: string, password: string, authServerUrl: string, realm: string, clientId: string): Observable<any> {
    const url = `${authServerUrl}/realms/${realm}/protocol/openid-connect/token`;

    // HttpHeaders and HttpParams are immutable => append does not set, needs to be assigned
    // e.g. params = params.append();
    const headers = new HttpHeaders().set('Content-type', 'application/x-www-form-urlencoded');
    const params = new HttpParams()
      .set('grant_type', 'password')
      .set('client_id', clientId)
      .set('username', username)
      .set('password', password);

    return KeycloakSupportService.http.post(url, params, {headers, withCredentials: true});
  }

  private autoLoginOrRouteToCheckAuth(keycloakOptions?: KeycloakOptions) {
    if (environment.keycloakConfig.autoLogin) {
      console.log("overriding keycloakOptions.config from environment.ts");
      if (!keycloakOptions) {
        keycloakOptions = {};
      }
      keycloakOptions.config = {
        url: environment.keycloakConfig.authServerUrl,
        realm: environment.keycloakConfig.realm,
        clientId: environment.keycloakConfig.clientId
      };
      keycloakOptions.initOptions = {
        token: null,
        refreshToken: null,
        idToken: null,
        checkLoginIframe: false,
        responseMode: "fragment"
      };
      console.log("autoLoginOrRouteToCheckAuth: invoking auto-login...");
      this.autoLogin(keycloakOptions);
    } else {
      console.log("autoLoginOrRouteToCheckAuth: routing to check-auth because token is expired and auto-login is disabled...");
      void this.router.navigate([RouteLink.CheckAuth]);
    }
  }

  private buildKeycloakOptionsForInit(realm: string): KeycloakOptions {
    return {
      config: {
        url: environment.keycloakConfig.authServerUrl,
        realm: realm,
        clientId: environment.keycloakConfig.clientId
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/assets/silent-check-sso.html'
      }
    };
  }

  private buildKeycloakLoginOptions(): Keycloak.KeycloakLoginOptions {
    const keycloakInstance = this.keycloakAngular.getKeycloakInstance();
    return {
      action: KeycloakActionType.Login,
      redirectUri: `${environment.rootUrl}/check-auth?realm=${keycloakInstance.realm}&action=${KeycloakActionType.Return}`
    };
  }

  private buildRedirectUriForLogout() {
    const keycloakInstance = this.keycloakAngular.getKeycloakInstance();
    const rootUrl = keycloakInstance.realm == DefaultOrg.Realm ? environment.rootUrlForCSR : environment.rootUrl;

    return `${rootUrl}/check-auth?realm=${keycloakInstance.realm}&action=${KeycloakActionType.Logout}`;
  }
}

export enum KeycloakSupportEventType {
  OnSsoIdle,
  OnSsoIdleWarning,
  OnSsoIdleExpire,
  OnSsoMax,
  OnSsoMaxWarning
}

export interface KeycloakSupportEvent {
  type: KeycloakSupportEventType;
  args?: any;
}
